/**
 * Offen oder abgehakt — und was das für die Reihenfolge der Übersicht heißt.
 *
 * ## Warum es diese Datei gibt
 *
 * Zwei Zeilen Bedingung im Markup wären kürzer und nicht prüfbar: Die Tests
 * dieses Projekts laufen ohne Browser (`tests/run.mjs`), und was in einer
 * `.svelte`-Datei steht, ist von dort unerreichbar. Die Entscheidung, welche
 * Reise wohin rückt, ist genau die Art Regel, die still kaputtgeht — eine
 * vertauschte Bedingung, und die gefahrenen Reisen stehen oben.
 *
 * ## Was der Schalter bedeutet — und was nicht
 *
 * Er ordnet die Übersicht und **sagt der geteilten Seite, dass die Reise
 * vorbei ist**. Der zweite Teil kam am 20.08.2026 dazu: Bis dahin sollte die
 * geteilte Seite sich ausdrücklich nicht ändern — bis auf Florians beendeter
 * Reise weiter „gefahren seit Beginn 867,2 km" stand und der Punkt mitten auf
 * der Route, weil der Fortschritt aus Tagebucheinträgen kommt und die
 * Heimfahrt keine hat. Mitleser fragten, ob er zu Hause angekommen sei.
 *
 * Gesperrt wird weiterhin nichts: Eine abgeschlossene Reise bleibt in vollem
 * Umfang bearbeitbar, und rückgängig ist der Schalter mit einem Tippen.
 * Deshalb steht vor dem Umlegen auch keine Rückfrage — Reibung gehört vor das
 * Unwiderrufliche, nicht vor das Umkehrbare.
 *
 * Reine Funktionen — keine Uhr, kein Netz, kein DOM.
 */

/** Alles, was hier gebraucht wird. Absichtlich nicht `Trip`: So lässt sich die
 *  Regel mit zwei Feldern prüfen statt mit einer ganzen Reise. */
export interface MitAbschluss {
  abgeschlossen_at?: string | null;
}

/**
 * Ist diese Reise abgehakt?
 *
 * `null` und `undefined` heißen beide offen — das eine ist der Normalfall aus
 * der Datenbank, das andere eine Antwort aus der Zeit vor der Spalte. Ein
 * leerer Zeitstempel zählt ebenfalls als offen: Ein `""` in einem Datumsfeld
 * ist kein Zeitpunkt, und daraus einen Abschluss zu lesen hieße, eine Reise
 * wegen eines Datenfehlers ans Ende zu schieben.
 */
export function istAbgeschlossen(t: MitAbschluss | null | undefined): boolean {
  return Boolean(t?.abgeschlossen_at?.trim());
}

/**
 * Was die geteilte Seite vom Abschluss mitbekommt.
 *
 * **`_am` und nicht `_at`**: Nach außen geht nur der Tag (`2026-08-17`), nicht
 * der Zeitstempel des Abhakens. Die beiden Felder heißen deshalb verschieden
 * — wer sie verwechselt, soll am Namen stolpern und nicht erst daran, dass
 * eine Uhrzeit auf einer fremden Seite steht.
 */
export interface MitAbschlusstag {
  abgeschlossen_am?: string | null;
}

/**
 * Ist die geteilte Seite im Fertig-Zustand?
 *
 * Dieselbe Regel wie `istAbgeschlossen`, nur auf dem Feld der öffentlichen
 * Antwort — und ausdrücklich an einer Stelle: In der Ansicht hing das an zwei
 * Ausdrücken (`Boolean(...)` für den Kopf, `!= null` für die Karte), und die
 * beiden sind sich bei `''` nicht einig. Dann stünde „Angekommen" über einer
 * Karte, auf der die halbe Route gestrichelt liegt.
 */
export function seiteAbgeschlossen(
  reise: MitAbschlusstag | null | undefined
): boolean {
  return Boolean(reise?.abgeschlossen_am?.trim());
}

/*
 * Die Zweiteilung der Übersicht (`gruppiereReisen`) stand bis zum 31.8.2026
 * hier. Sie ist in `reisegruppen.ts` aufgegangen, weil aus zwei Blöcken vier
 * Plätze geworden sind: die laufende Reise einzeln obenauf, darunter „Geplante
 * Reisen", „Reisen in Planung" und „Abgeschlossen". Ihre Zusagen sind
 * mitgewandert und stehen dort — insbesondere die, dass die Reihenfolge
 * innerhalb einer Gruppe bleibt, wie sie hereinkam.
 *
 * Zurückgelassen wurde sie **nicht** als zweiter Weg: Zwei Funktionen, die
 * dieselbe Liste verschieden ordnen, wären eine Gelegenheit, versehentlich die
 * alte zu rufen — und die kennt weder Phase noch Planungsstand.
 */
